import { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  message,
  Popconfirm,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd'
import type { UploadFile } from 'antd'
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  RightOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { projectsApi } from '@/api/projects'
import { filesApi } from '@/api/files'
import { jobsApi } from '@/api/jobs'
import {
  JOB_STATE_COLORS,
  JOB_STATE_LABELS,
  WORKFLOW_STEP_LABELS,
  isJobActive,
  isJobInFlight,
  jobActionLabel,
  type CadFile,
  type Job,
} from '@/types/api'

const { Title, Text } = Typography

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([])
  const [uploadingCount, setUploadingCount] = useState(0)

  const { data: project } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id!),
    enabled: !!id,
  })

  const { data: files, isLoading: filesLoading } = useQuery({
    queryKey: ['files', id],
    queryFn: () => filesApi.list(id!),
    enabled: !!id,
  })

  const { data: jobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', id],
    queryFn: () => jobsApi.list(id!),
    enabled: !!id,
    refetchInterval: (q) => {
      const list = q.state.data
      if (!list?.some((j) => isJobInFlight(j.state))) return false
      return 2000
    },
  })

  const inFlightJob = jobs?.find((j) => isJobInFlight(j.state))

  const createJobMutation = useMutation({
    mutationFn: (fileIds: string[]) => jobsApi.create(id!, { file_ids: fileIds }),
    onSuccess: (job) => {
      qc.invalidateQueries({ queryKey: ['jobs', id] })
      message.success('已触发算量任务，关闭页面后可从任务列表继续查看')
      navigate(`/jobs/${job.id}`)
    },
    onError: (err: unknown) => {
      const apiError = (err as { apiError?: { code?: string; details?: Record<string, unknown> } }).apiError
      const jobId = apiError?.details?.job_id
      qc.invalidateQueries({ queryKey: ['jobs', id] })
      if (apiError?.code === 'conflict' && typeof jobId === 'string') {
        message.warning('已有未完成任务，已为你打开该任务。若要重跑请先停止')
        navigate(`/jobs/${jobId}`)
      }
    },
  })

  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => jobsApi.cancel(jobId),
    onSuccess: (stopped) => {
      message.success('任务已停止，可以开始新的算量')
      qc.setQueryData(['jobs', id], (old: Job[] | undefined) =>
        old?.map((j) => (j.id === stopped.id ? stopped : j)),
      )
      qc.invalidateQueries({ queryKey: ['jobs', id] })
    },
  })

  const cancelInFlightMutation = useMutation({
    mutationFn: () => jobsApi.cancelInFlight(id!),
    onSuccess: (stopped) => {
      message.success(
        stopped.length > 0 ? `已停止 ${stopped.length} 个任务，可以重新算量` : '没有进行中的任务',
      )
      const byId = new Map(stopped.map((j) => [j.id, j]))
      qc.setQueryData(['jobs', id], (old: Job[] | undefined) =>
        old?.map((j) => byId.get(j.id) ?? j),
      )
      qc.invalidateQueries({ queryKey: ['jobs', id] })
    },
  })

  const deleteFileMutation = useMutation({
    mutationFn: (fileId: string) => filesApi.remove(id!, fileId),
    onSuccess: (_, fileId) => {
      message.success('已删除')
      setSelectedFileIds((ids) => ids.filter((x) => x !== fileId))
      qc.invalidateQueries({ queryKey: ['files', id] })
    },
  })

  const deleteSelectedFiles = async () => {
    const ids = [...selectedFileIds]
    if (ids.length === 0) return
    const results = await Promise.allSettled(ids.map((fid) => filesApi.remove(id!, fid)))
    const ok = results.filter((r) => r.status === 'fulfilled').length
    const failed = results.length - ok
    if (ok > 0) {
      const removed = new Set(ids.filter((_, i) => results[i].status === 'fulfilled'))
      setSelectedFileIds((cur) => cur.filter((x) => !removed.has(x)))
      qc.invalidateQueries({ queryKey: ['files', id] })
      message.success(`已删除 ${ok} 个文件`)
    }
    if (failed > 0 && ok === 0) {
      message.error('文件删除失败')
    }
  }

  const uploadCustomRequest = async (options: {
    file: string | Blob
    onSuccess?: (body: unknown) => void
    onError?: (err: Error) => void
    onProgress?: (event: { percent: number }) => void
  }) => {
    setUploadingCount((n) => n + 1)
    try {
      const created = await filesApi.upload(
        id!,
        options.file as File,
        (pct) => options.onProgress?.({ percent: pct }),
      )
      qc.invalidateQueries({ queryKey: ['files', id] })
      options.onSuccess?.(created)
    } catch (e) {
      options.onError?.(e as Error)
    } finally {
      setUploadingCount((n) => Math.max(0, n - 1))
    }
  }

  const canStart =
    selectedFileIds.length > 0 &&
    uploadingCount === 0 &&
    !jobsLoading &&
    jobs !== undefined

  return (
    <>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/projects')}
        style={{ padding: 0, marginBottom: 8 }}
      >
        返回项目列表
      </Button>
      <Title level={3} style={{ marginTop: 0 }}>
        {project?.name || '项目详情'}
      </Title>

      {project && (
        <Descriptions column={4} size="small" style={{ marginBottom: 24 }}>
          <Descriptions.Item label="品牌">
            <Tag color="blue">{project.brand_id}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="模板版本">{project.template_version}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color="green">{project.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="项目参数">
            {Object.keys(project.param_overrides).length > 0 ? (
              <Space size="small" wrap>
                {Object.entries(project.param_overrides).map(([k, v]) => (
                  <Tag key={k}>
                    {k}={v}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">未设置</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      )}

      <Card
        title={
          <Space>
            <span>算量任务</span>
            <Tag>{jobs?.length ?? 0} 个</Tag>
          </Space>
        }
        style={{ marginBottom: 24 }}
        extra={
          <Text type="secondary">同一项目同时只跑一个未完成任务；点「开始算量」会停掉旧任务再开新的</Text>
        }
      >
        {jobs && jobs.length === 0 && !jobsLoading ? (
          <Empty
            description="还没有算量任务。上传并勾选文件后点「开始算量」"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table<Job>
            loading={jobsLoading}
            dataSource={jobs || []}
            rowKey="id"
            pagination={false}
            size="small"
            onRow={(record) => ({
              onClick: () => navigate(`/jobs/${record.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: '任务',
                dataIndex: 'id',
                width: 120,
                render: (jid: string) => <Text code>{jid.slice(0, 8)}</Text>,
              },
              {
                title: '状态',
                dataIndex: 'state',
                width: 110,
                render: (s: Job['state']) => (
                  <Tag color={JOB_STATE_COLORS[s]}>{JOB_STATE_LABELS[s] || s}</Tag>
                ),
              },
              {
                title: '当前步骤',
                dataIndex: 'current_skill',
                render: (skill: string | null, record) => {
                  if (!skill || record.state === 'cancelled') {
                    return <Text type="secondary">-</Text>
                  }
                  return WORKFLOW_STEP_LABELS[skill] || skill
                },
              },
              {
                title: '进度',
                dataIndex: 'progress',
                width: 180,
                render: (pct: number, record) => (
                  <Progress
                    percent={pct}
                    size="small"
                    status={
                      record.state === 'failed'
                        ? 'exception'
                        : ['success', 'partial_success'].includes(record.state)
                          ? 'success'
                          : isJobActive(record.state)
                            ? 'active'
                            : 'normal'
                    }
                  />
                ),
              },
              {
                title: '开始时间',
                key: 'time',
                width: 170,
                render: (_, record) => (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.started_at
                      ? new Date(record.started_at).toLocaleString('zh-CN')
                      : record.created_at
                        ? new Date(record.created_at).toLocaleString('zh-CN')
                        : '-'}
                  </Text>
                ),
              },
              {
                title: '',
                key: 'action',
                width: 180,
                align: 'right' as const,
                render: (_, record) => (
                  <Space size={0} onClick={(e) => e.stopPropagation()}>
                    {isJobInFlight(record.state) && (
                      <Popconfirm
                        title="停止后才能开始新任务，确定停止？"
                        okText="停止"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => cancelJobMutation.mutate(record.id)}
                      >
                        <Button
                          type="link"
                          size="small"
                          danger
                          icon={<StopOutlined />}
                          loading={cancelJobMutation.isPending}
                        >
                          停止
                        </Button>
                      </Popconfirm>
                    )}
                    <Button
                      type="link"
                      size="small"
                      icon={<RightOutlined />}
                      onClick={() => navigate(`/jobs/${record.id}`)}
                    >
                      {jobActionLabel(record.state)}
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card
        title="1. 上传 CAD 文件"
        style={{ marginBottom: 24 }}
        extra={<Text type="secondary">支持 DXF；DWG 需 ODA 授权（V1 暂不支持）</Text>}
      >
        <Upload.Dragger
          multiple
          accept=".dxf,.dwg"
          showUploadList={{ showRemoveIcon: false }}
          customRequest={uploadCustomRequest as never}
          onChange={(info) => {
            const done = info.fileList.filter((f: UploadFile) => f.status === 'done')
            if (done.length !== info.fileList.length) return
          }}
        >
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined style={{ fontSize: 40, color: '#1F4E78' }} />
          </p>
          <p className="ant-upload-text">点击或拖拽 CAD 文件到此上传</p>
          <p className="ant-upload-hint">
            允许一次多张（含平面 / 立面 / 天花 / 大样图），系统会自动选主平面
          </p>
        </Upload.Dragger>
      </Card>

      <Card
        title={
          <Space>
            <span>2. 已上传的文件</span>
            <Tag color="blue">{files?.length ?? 0} 个</Tag>
          </Space>
        }
        style={{ marginBottom: 24 }}
        extra={
          selectedFileIds.length > 0 ? (
            <Space>
              <Text type="secondary">已选 {selectedFileIds.length} 个用于算量</Text>
              <Popconfirm
                title={`确定删除已选的 ${selectedFileIds.length} 个文件？`}
                description={
                  inFlightJob
                    ? '请先停止未完成任务再删除'
                    : '已被算量任务引用的文件无法删除'
                }
                okText="删除"
                cancelText="取消"
                okButtonProps={{ danger: true, disabled: !!inFlightJob }}
                onConfirm={() => {
                  if (inFlightJob) {
                    message.warning('请先停止未完成任务再删除文件')
                    return
                  }
                  void deleteSelectedFiles()
                }}
              >
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={!!inFlightJob || deleteFileMutation.isPending}
                >
                  删除所选
                </Button>
              </Popconfirm>
            </Space>
          ) : undefined
        }
      >
        {files && files.length === 0 ? (
          <Empty description="尚未上传文件" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table<CadFile>
            loading={filesLoading}
            dataSource={files || []}
            rowKey="id"
            pagination={false}
            size="small"
            rowSelection={{
              selectedRowKeys: selectedFileIds,
              onChange: (keys) => setSelectedFileIds(keys as string[]),
            }}
            columns={[
              { title: '文件名', dataIndex: 'filename', ellipsis: true },
              {
                title: '格式',
                dataIndex: 'format',
                width: 80,
                render: (fmt) => <Tag>{fmt}</Tag>,
              },
              {
                title: '大小',
                dataIndex: 'size_bytes',
                width: 100,
                render: (b: number) => `${(b / 1024 / 1024).toFixed(2)} MB`,
                align: 'right' as const,
              },
              {
                title: 'SHA',
                dataIndex: 'sha256',
                width: 100,
                render: (s: string) => (
                  <Text code style={{ fontSize: 11 }}>
                    {s.slice(0, 8)}
                  </Text>
                ),
              },
              {
                title: '',
                key: 'action',
                width: 80,
                align: 'right' as const,
                render: (_, record) => (
                  <Popconfirm
                    title={`确定删除「${record.filename}」？`}
                    description={
                      inFlightJob
                        ? '请先停止未完成任务再删除'
                        : '已被算量任务引用的文件无法删除'
                    }
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true, disabled: !!inFlightJob }}
                    onConfirm={() => {
                      if (inFlightJob) {
                        message.warning('请先停止未完成任务再删除文件')
                        return
                      }
                      deleteFileMutation.mutate(record.id)
                    }}
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!!inFlightJob}
                      loading={deleteFileMutation.isPending && deleteFileMutation.variables === record.id}
                    >
                      删除
                    </Button>
                  </Popconfirm>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card title="3. 触发算量">
        {uploadingCount > 0 && (
          <Alert
            message={`还有 ${uploadingCount} 个文件上传中，请等待完成后再触发`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {selectedFileIds.length === 0 && (files?.length || 0) > 0 && (
          <Alert
            message="请在上表勾选要参与算量的文件（至少 1 个，通常勾主平面）"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {inFlightJob && (
          <Alert
            message={
              inFlightJob.state === 'wait_review'
                ? '当前有待复核任务。点「开始算量」会停止它并开新任务；要继续复核请点「查看该任务」'
                : `当前有未完成任务（${JOB_STATE_LABELS[inFlightJob.state]}）。点「开始算量」会停止它并重新开始`
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Space>
                <Button size="small" onClick={() => navigate(`/jobs/${inFlightJob.id}`)}>
                  查看该任务
                </Button>
                <Popconfirm
                  title="停止全部未完成任务后即可重新算量，确定停止？"
                  okText="停止"
                  cancelText="取消"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => cancelInFlightMutation.mutate()}
                >
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    loading={cancelInFlightMutation.isPending}
                  >
                    停止后重跑
                  </Button>
                </Popconfirm>
              </Space>
            }
          />
        )}
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          size="large"
          disabled={!canStart}
          loading={createJobMutation.isPending}
          onClick={() => createJobMutation.mutate(selectedFileIds)}
        >
          开始算量（{selectedFileIds.length} 个文件）
        </Button>
        {uploadingCount > 0 && (
          <Progress
            percent={0}
            status="active"
            showInfo={false}
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    </>
  )
}
